import { useResourceParams } from '@refinedev/core';
import { Form, FormProps } from 'antd';
import { useEffect, useState } from 'react';

import { PackageItem } from '@/model/Logistic/PackageItem/PackageItem';
import { SHIPPING_ITEM_REFERENCE } from '@/model/Logistic/Shipment/ShipmentReference';
import {
  SERVICE_ORDER_ITEM_RESOLUTION,
  ServiceOrderItem,
} from '@/model/ServiceOrder/ServiceOrderItem';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';

const useServiceOrderItems = (
  serviceOrderItems: ServiceOrderItem[],
  setServiceOrderItems: React.Dispatch<React.SetStateAction<ServiceOrderItem[]>>,
  formProps?: FormProps,
) => {
  const tickets = Form.useWatch('tickets', formProps?.form);
  const { packageItemRepository, serviceOrderRepository } = useRepositoriesContext();
  const [shippedItems, setShippedItems] = useState<PackageItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // Carga de items
  const { action, id: serviceOrderId } = useResourceParams();
  const editMode = action === 'edit';
  const [itemsLoaded, setItemsLoaded] = useState(false);

  useEffect(() => {
    let isActive = true;

    if (!isActive || tickets == undefined) {
      return;
    }

    if (itemsLoaded) {
      if (tickets?.length > 0) {
        setIsLoading(true);
        packageItemRepository
          .getShippedItemsFromTickets(tickets)
          .then(items => {
            if (!isActive) {
              return;
            }

            setServiceOrderItemsForShippedItems(serviceOrderItems, items);
            setShippedItems(items);
          })
          .catch(error => {
            console.error('Error fetching shipped items from tickets:', error);
          })
          .finally(() => {
            if (isActive) {
              setIsLoading(false);
            }
          });
      } else {
        setIsLoading(false);
        setShippedItems([]);
      }
    } else {
      const promises: Promise<any>[] = [];

      if (!editMode || !serviceOrderId) {
        promises.push(Promise.resolve([] as ServiceOrderItem[]));
      } else {
        promises.push(
          serviceOrderRepository.getServiceOrderItems(parseInt(serviceOrderId as string, 10)),
        );
      }

      if (tickets?.length > 0) {
        promises.push(packageItemRepository.getShippedItemsFromTickets(tickets));
      } else {
        promises.push(Promise.resolve([] as PackageItem[]));
      }

      Promise.all(promises)
        .then(([items, shippedItems]) => {
          setShippedItems(shippedItems);
          setServiceOrderItemsForShippedItems(items, shippedItems);
          setItemsLoaded(true);
        })
        .catch(error => {
          console.error('Error fetching service order items:', error);
        });
    }

    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets]);

  const setServiceOrderItemsForShippedItems = (
    serviceOrderItems: ServiceOrderItem[],
    shippedItems: PackageItem[],
  ) => {
    const newServiceOrderItems: ServiceOrderItem[] = [];
    const technicianItems = serviceOrderItems.filter(so => !so.package_item);

    shippedItems.forEach(item => {
      const existing = serviceOrderItems.find(soItem => {
        const packageItemId =
          typeof soItem.package_item === 'number' ? soItem.package_item : soItem.package_item?.id;

        return packageItemId === item.id;
      });
      const soItem: ServiceOrderItem = existing
        ? { ...existing, toDelete: false }
        : ({
            product: item.product,
            device: item.device,
            unidad: item.unidad,
            package_item: item.id,
            used_amount:
              item.item_shipped_reference === SHIPPING_ITEM_REFERENCE.SIN_DEVOLUCION ||
              item.item_shipped_reference === SHIPPING_ITEM_REFERENCE.VENTA_REPUESTO
                ? item.execute_amount
                : 0,
            stock_technician_amount: 0,
            discarded_amount: 0,
            to_return_amount: 0,
            name: item.name,
            in_warranty: item.in_warranty_period,
            resolution_status:
              item.item_shipped_reference == SHIPPING_ITEM_REFERENCE.SUJETO_PERITAJE
                ? SERVICE_ORDER_ITEM_RESOLUTION.RESOLUTION_PERITAGE
                : undefined,
          } as ServiceOrderItem);

      newServiceOrderItems.push(soItem);
    });

    setServiceOrderItems([...newServiceOrderItems, ...technicianItems]);
  };

  return { shippedItems, isLoading, tickets: tickets ?? [] };
};

export default useServiceOrderItems;
